import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Search, Send, Users, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationSummary, Message, MessagePage, ReactionEmoji } from '@up2d8/contracts';
import {
  AttachmentRowBody,
  Avatar,
  attachmentRowClass,
  cn,
  fileSizeLabel,
  relativeTime,
} from '@up2d8/ui';
import { useFileUrl } from '../../../shared/use-upload';
import { useSession } from '../../../shared/session';
import { uiLocale } from '../../../shared/format';
import {
  MessageActionButtons,
  ReactionPicker,
  ReactionPills,
  ReplyComposerBar,
  ReplyQuote,
  jumpToMessage,
  useReactToMessage,
} from '../chat-message-extras';

/**
 * Paneel 2: het gesprek (replica-spec §4).
 *
 * Drie delen onder elkaar: de kop, het berichtengebied dat als enige scrolt, en de invoerbalk.
 *
 * **Wat er bewust niet in zit** (zie `docs/design/deviations.md`):
 *
 * - de **typ-indicator** — aanwezigheid is een feit over een persoon, en dat maakt van een vraag
 *   een verwachting over antwoordtijd (ADR-0050 punt 2, bevestigd in ADR-0068);
 * - de **"5 online"-regel** in de kop, om dezelfde reden; er staat het ledenaantal;
 * - een **emoji-knop in de invoerbalk** — emoji's typen kan met het toetsenbord van het toestel.
 *
 * Wel sinds BER-8: bij het aanwijzen van een ballon twee knoppen, **reageren** (zes emoji's) en
 * **antwoorden** (het bericht komt als citaat boven je antwoord).
 */
export interface DeskConversationProps {
  conversation: ConversationSummary;
  /**
   * Of het infopaneel open staat, en hoe je het omzet.
   *
   * Beide **optioneel**, en dat is geen slordigheid: zolang paneel 3 nog niet bestaat (BER-7),
   * hoort de knop er niet te zijn. Een knop die in de actieve toestand springt terwijl er niets
   * opengaat, is erger dan geen knop — dezelfde regel als bij het "…"-menu op de berichtkaart.
   */
  infoOpen?: boolean;
  onToggleInfo?: () => void;
  /**
   * Het zoekveld in de kop, van buitenaf bestuurd.
   *
   * De actie "Zoeken" in het infopaneel moet hetzelfde veld openen als de knop in de kop — anders
   * zijn het twee zoekvelden die elkaar niet kennen. Daarom woont die toestand bij de pagina en
   * niet hier.
   */
  search?: string | null;
  onSearchChange?: (value: string | null) => void;
}

export function DeskConversation({
  conversation,
  infoOpen = false,
  onToggleInfo,
  search: searchProp,
  onSearchChange,
}: DeskConversationProps) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  const [failed, setFailed] = useState(false);
  /** Het bericht waarop je antwoordt, zolang de balk boven het invoerveld staat. */
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const react = useReactToMessage(conversation.id);

  const [ownSearch, setOwnSearch] = useState<string | null>(null);
  // Bestuurd van buitenaf wanneer de pagina dat wil, anders van zichzelf.
  const search = searchProp !== undefined ? searchProp : ownSearch;
  const setSearch = (value: string | null) => {
    if (onSearchChange) onSearchChange(value);
    else setOwnSearch(value);
  };

  const scroller = useRef<HTMLDivElement>(null);
  /** Stond de lezer onderaan vlak vóór deze render? Bepaalt of hij meegeschoven mag worden. */
  const wasAtBottom = useRef(true);

  /*
    Een ander gesprek openen laat niets van het vorige meelopen: geen antwoord, geen half getypte
    zin en geen foutmelding. Anders gaat "Versturen" met de tekst voor de ene collega naar de
    andere. Het nieuwe gesprek opent onderaan.
  */
  useEffect(() => {
    setReplyTo(null);
    setDraft('');
    setFailed(false);
    setOwnSearch(null);
    wasAtBottom.current = true;
  }, [conversation.id]);

  const messages = useQuery({
    queryKey: ['conversation', conversation.id, 'messages', search ?? ''],
    queryFn: async (): Promise<MessagePage> => {
      const { data, error } = await api.GET('/api/v1/conversations/{conversationId}/messages', {
        params: {
          path: { conversationId: conversation.id },
          query: search ? { q: search } : {},
        },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as MessagePage;
    },
    refetchInterval: 20_000,
  });

  // De server levert nieuwste eerst; het scherm leest van oud naar nieuw.
  const items = useMemo(() => [...(messages.data?.items ?? [])].reverse(), [messages.data]);

  /*
    De scrollpositie (§4.2).

    Bij openen onderaan, en daarna alleen meeschuiven als de lezer er al stond. Wie omhoog
    gescrold is om iets terug te lezen, hoort niet weggeduwd te worden door een bericht dat
    binnenkomt — dat is het verschil tussen een chat die werkt en een chat die je het terugzoeken
    onmogelijk maakt.

    `useLayoutEffect` en niet `useEffect`: na het schilderen scrollen geeft een zichtbare sprong.
  */
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) return;
    if (wasAtBottom.current) element.scrollTop = element.scrollHeight;
  }, [items]);

  const onScroll = () => {
    const element = scroller.current;
    if (!element) return;
    // Een marge van 40 px: wie bijna onderaan staat, bedoelt onderaan.
    wasAtBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
  };

  const markRead = useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/conversations/{conversationId}/read', {
        params: { path: { conversationId: conversation.id } },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversations'] }),
  });

  /*
    Het gesprek openen zet de teller op nul.

    Afhankelijk van het id én de teller: zonder dat tweede blijft de badge staan wanneer er een
    bericht binnenkomt terwijl het gesprek al openstaat. `markRead` staat er met opzet niet bij —
    die functie wordt elke render opnieuw gemaakt en zou een lus opleveren.
  */
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  useEffect(() => {
    if (conversation.unreadCount > 0) markReadRef.current.mutate();
  }, [conversation.id, conversation.unreadCount]);

  const send = useMutation({
    mutationFn: async (body: string): Promise<Message> => {
      const { data, error } = await api.POST('/api/v1/conversations/{conversationId}/messages', {
        params: { path: { conversationId: conversation.id } },
        body: { body, attachmentFileIds: [], replyToId: replyTo?.id ?? null },
      });
      if (error || !data) throw error ?? new Error('Versturen mislukt');
      return data as Message;
    },
    onSuccess: () => {
      // Pas wissen als het gelukt is. Wie zijn zin kwijtraakt omdat het netwerk hikte, typt hem
      // niet opnieuw — hij pakt zijn telefoon.
      setDraft('');
      setFailed(false);
      setReplyTo(null);
      wasAtBottom.current = true;
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversation.id] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => setFailed(true),
  });

  const trimmed = draft.trim();

  return (
    <div
      data-testid="chat-conversation"
      className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-card border border-border-light bg-surface shadow-sm"
    >
      {/* §4.1 — de kop */}
      <div className="flex items-center gap-3 border-b border-border-light px-5 py-3.5">
        {conversation.kind === 'group' ? (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-brand-primary-tint text-brand-primary-dark">
            <Users aria-hidden className="h-5 w-5" />
          </span>
        ) : (
          <Avatar name={conversation.title} size="lg" />
        )}

        <div className="min-w-0 flex-1">
          <h2 className="truncate font-heading text-[15px] font-semibold text-text-primary">
            {conversation.title}
          </h2>
          {/* Het ledenaantal, en géén "5 online": aanwezigheid is een feit over een persoon. */}
          {conversation.kind === 'group' && (
            <p className="text-micro text-text-muted">
              {t('messaging.memberCount', { count: conversation.participantCount })}
            </p>
          )}
        </div>

        <button
          type="button"
          data-testid="search-in-conversation"
          aria-pressed={search !== null}
          aria-label={t('messaging.searchInConversation')}
          onClick={() => setSearch(search === null ? '' : null)}
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-pill transition-colors',
            search !== null
              ? 'bg-brand-primary-tint text-brand-primary'
              : 'text-text-secondary hover:bg-background hover:text-brand-primary',
          )}
        >
          <Search aria-hidden className="h-[18px] w-[18px]" />
        </button>

        {onToggleInfo && (
          <button
            type="button"
            data-testid="toggle-info"
            aria-pressed={infoOpen}
            aria-label={t('messaging.conversationInfo')}
            onClick={onToggleInfo}
            /* `hidden full:flex`: het infopaneel bestaat pas vanaf 1400 px (§7). Daaronder zou de
               knop omspringen zonder dat er iets opengaat. */
            className={cn(
              'hidden h-9 w-9 full:flex shrink-0 items-center justify-center rounded-pill transition-colors',
              infoOpen
                ? 'bg-brand-primary-tint text-brand-primary'
                : 'text-text-secondary hover:bg-background hover:text-brand-primary',
            )}
          >
            <Info aria-hidden className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>

      {/* Het zoekveld verschijnt pas als er gezocht wordt (§4.1 kent alleen de knop). */}
      {search !== null && (
        <div className="flex items-center gap-2 border-b border-border-light px-5 py-2">
          <input
            autoFocus
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            data-testid="conversation-message-search"
            aria-label={t('messaging.searchInConversation')}
            placeholder={t('messaging.searchInConversationPlaceholder')}
            className="h-9 flex-1 rounded-pill border border-border bg-background px-4 text-small text-text-primary outline-none focus:border-brand-primary focus:bg-surface focus:shadow-[0_0_0_4px_rgba(0,178,221,0.1)]"
          />
          <button
            type="button"
            onClick={() => setSearch(null)}
            aria-label={t('common.cancel')}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-text-secondary hover:bg-background"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* §4.2 — het berichtengebied; het enige dat scrolt */}
      <div
        ref={scroller}
        onScroll={onScroll}
        data-testid="chat-messages"
        /* `bg-background-warm` is #F8F9FB uit §4.2 — net iets lichter dan `--background`
           (#F5F7FA), dat de hover van een rij is. Twee tinten die op een schermafdruk hetzelfde
           lijken en in de tokens een ander doel hebben. */
        className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-background-warm px-5 pb-5 pt-2"
      >
        {messages.isPending ? (
          <p className="py-4 text-small text-text-muted">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="py-4 text-center text-small text-text-muted">
            {search ? t('messaging.noMessageMatches') : t('messaging.noMessages')}
          </p>
        ) : (
          items.map((message, index) => (
            <MessageRow
              key={message.id}
              message={message}
              previous={items[index - 1]}
              isGroup={conversation.kind === 'group'}
              onReact={(emoji) => react.mutate({ messageId: message.id, emoji })}
              onReply={() => {
                setReplyTo(message);
                input.current?.focus();
              }}
              onJump={(id) => jumpToMessage(scroller.current, id)}
            />
          ))
        )}
      </div>

      {/* §4.3 — de invoerbalk */}
      <form
        className="border-t border-border-light px-5 py-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed) send.mutate(trimmed);
        }}
      >
        {failed && (
          <p role="alert" data-testid="send-failed" className="mb-2 text-small text-danger">
            {t('messaging.sendFailed')}
          </p>
        )}
        {replyTo && (
          <ReplyComposerBar message={replyTo} onCancel={() => setReplyTo(null)} className="mb-2" />
        )}
        <div className="flex items-center gap-2.5">
          {/* De bijlageknop wacht op het uploadscherm van BER-6b; tot dan is hij er niet, want
              een knop die niets doet is erger dan geen knop. */}
          <div className="flex flex-1 items-center gap-2 rounded-pill border border-transparent bg-background py-[3px] pl-4 pr-[3px] focus-within:border-brand-primary focus-within:bg-surface focus-within:shadow-[0_0_0_4px_rgba(0,178,221,0.1)]">
            <input
              ref={input}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && replyTo) setReplyTo(null);
              }}
              data-testid="chat-input"
              /* Een eigen label, en niet hetzelfde als op de telefoon: beide indelingen zitten in
                 dezelfde DOM, en twee velden met dezelfde naam maakt een schermlezer net zo
                 besluiteloos als een test. */
              aria-label={t('messaging.composeLabel', { name: conversation.title })}
              placeholder={t('messaging.composePlaceholder', { name: conversation.title })}
              className="min-w-0 flex-1 border-none bg-transparent py-2 text-small text-text-primary outline-none placeholder:text-text-muted"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={!trimmed || send.isPending}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-brand-primary px-[18px] py-2 font-heading text-caption font-semibold text-text-on-dark transition-colors hover:bg-brand-primary-dark disabled:opacity-50"
            >
              {t('messaging.send')}
              <Send aria-hidden className="h-[13px] w-[13px]" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/** De datumscheiding boven het eerste bericht van een nieuwe dag (§4.2). */
function dayKey(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function MessageRow({
  message,
  previous,
  isGroup,
  onReact,
  onReply,
  onJump,
}: {
  message: Message;
  previous: Message | undefined;
  isGroup: boolean;
  onReact: (emoji: ReactionEmoji | null) => void;
  onReply: () => void;
  onJump: (messageId: string) => void;
}) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const now = new Date();

  const newDay = !previous || dayKey(previous.createdAt) !== dayKey(message.createdAt);

  if (message.kind === 'system') {
    return (
      <>
        {newDay && <DaySeparator iso={message.createdAt} now={now} />}
        <p
          data-testid="chat-system"
          className="mb-0.5 mt-1.5 text-center text-micro text-text-muted"
        >
          {message.systemEvent === 'participant_added'
            ? t('messaging.systemAdded', {
                actor: message.senderDisplayName,
                person: message.systemAboutName ?? '',
              })
            : t('messaging.systemRemoved', {
                actor: message.senderDisplayName,
                person: message.systemAboutName ?? '',
              })}
        </p>
      </>
    );
  }

  /*
    Groepering (§4.2): een bericht dat direct volgt op een bericht van dezelfde afzender staat
    dichter tegen het vorige aan, verliest zijn naamregel, en krijgt een lege ruimte van 28 px
    waar de avatar stond. Dat laatste is het detail dat een chat er gebouwd laat uitzien in plaats
    van gestapeld.

    Een datumscheiding of een systeemregel breekt de groepering: dan staat er iets tussen.
  */
  const grouped =
    !newDay &&
    previous !== undefined &&
    previous.kind === 'text' &&
    previous.senderId === message.senderId;

  const time = new Date(message.createdAt).toLocaleTimeString(uiLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });

  /*
    Reageren en antwoorden (BER-8): twee knoppen naast de ballon, zichtbaar bij aanwijzen of
    wanneer de focus erin staat. Aan de binnenkant van het gesprek — links van je eigen ballon,
    rechts van die van een ander — zodat ze nooit over de rand van het paneel vallen.
  */
  const actions = (
    <div
      className={cn(
        'relative flex shrink-0 items-center gap-0.5 self-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100',
        pickerOpen && 'opacity-100',
      )}
    >
      <MessageActionButtons
        pickerOpen={pickerOpen}
        onOpenPicker={() => setPickerOpen((open) => !open)}
        onReply={onReply}
      />
      {pickerOpen && (
        <ReactionPicker
          current={message.myReaction}
          onClose={() => setPickerOpen(false)}
          onPick={(emoji) => {
            setPickerOpen(false);
            onReact(message.myReaction === emoji ? null : emoji);
          }}
          className={cn('absolute bottom-full z-10 mb-1', message.mine ? 'right-0' : 'left-0')}
        />
      )}
    </div>
  );

  return (
    <>
      {newDay && <DaySeparator iso={message.createdAt} now={now} />}
      <div
        data-testid="chat-message"
        data-message-id={message.id}
        data-mine={message.mine ? 'true' : 'false'}
        data-grouped={grouped ? 'true' : 'false'}
        className={cn(
          'group flex w-[min(72%,560px)] max-w-[min(72%,560px)] items-start gap-2',
          grouped ? 'mt-[3px]' : 'mt-3',
          message.mine && 'self-end justify-end',
        )}
      >
        {message.mine && actions}
        {!message.mine &&
          (grouped ? (
            // De lege ruimte waar de avatar stond: zonder dit springt de ballon naar links.
            <span aria-hidden className="w-7 shrink-0" />
          ) : (
            <Avatar name={message.senderDisplayName} size="sm" className="h-7 w-7 text-micro" />
          ))}

        <div className={cn('min-w-0', message.mine && 'flex flex-col items-end')}>
          {!message.mine && !grouped && isGroup && (
            <p className="mx-1 mb-1 font-heading text-caption font-semibold text-text-primary">
              {message.senderDisplayName}
            </p>
          )}

          <div
            data-testid="chat-bubble"
            className={cn(
              'px-3.5 py-2.5 text-small leading-[1.5]',
              message.mine
                ? 'rounded-[18px] rounded-br-[6px] bg-brand-primary text-text-on-dark shadow-[0_2px_8px_rgba(0,178,221,0.25)]'
                : 'rounded-[18px] rounded-tl-[6px] border border-border-light bg-surface text-text-primary shadow-sm',
            )}
          >
            {message.replyTo && (
              <ReplyQuote reply={message.replyTo} mine={message.mine} onJump={onJump} />
            )}

            <span className="whitespace-pre-wrap break-words">{message.body}</span>

            {message.attachments.map((file) => (
              <ChatAttachment key={file.fileId} file={file} />
            ))}
          </div>

          {/* De reactie-pills overlappen de onderrand van de ballon (§4.2). */}
          <ReactionPills
            message={message}
            onReact={onReact}
            className={cn('-mt-2', message.mine ? 'mr-3 justify-end' : 'ml-3')}
          />

          <div
            className={cn(
              'mx-1 mt-[3px] flex items-center gap-1.5 text-[10px] text-text-muted',
              message.mine && 'justify-end',
            )}
          >
            <span>{time}</span>
            {message.readByCount !== null && (
              <span
                data-testid="read-by"
                className="font-heading font-medium text-brand-primary-dark"
              >
                {t('messaging.readBy', { count: message.readByCount })}
              </span>
            )}
          </div>
        </div>

        {!message.mine && actions}
      </div>
    </>
  );
}

function DaySeparator({ iso, now }: { iso: string; now: Date }) {
  const { t } = useTranslation();
  const dagen =
    Math.floor(new Date(now.toDateString()).getTime() / 86_400_000) -
    Math.floor(new Date(new Date(iso).toDateString()).getTime() / 86_400_000);

  const label =
    dagen === 0
      ? t('messaging.today')
      : relativeTime(iso, now, {
          yesterday: t('feed.yesterday'),
          justNow: '',
          minutesAgo: '',
          hoursAgo: '',
        });

  return (
    <p
      data-testid="chat-day"
      className="mx-auto mb-1.5 mt-3.5 rounded-pill border border-border-light bg-surface px-3 py-1 font-heading text-micro font-medium text-text-muted shadow-sm"
    >
      {label}
    </p>
  );
}

function ChatAttachment({ file }: { file: Message['attachments'][number] }) {
  const { t } = useTranslation();
  const fileUrl = useFileUrl();

  return (
    <button
      type="button"
      data-testid="chat-attachment"
      className={cn('mt-2 min-w-[260px]', attachmentRowClass)}
      onClick={async () => {
        const link = await fileUrl(file.fileId);
        if (link) window.open(link, '_blank', 'noopener,noreferrer');
      }}
    >
      <AttachmentRowBody
        name={file.fileName}
        isImage={file.isImage}
        meta={fileSizeLabel(file.sizeBytes)}
      />
      <span className="sr-only">{t('feed.openAttachment')}</span>
    </button>
  );
}
